import {
  type CreatePriceValidationErrorInfo,
  type CreatePriceValidationProblemDetails,
  PriceStatus,
  type SaveCreatePriceSessionRequest,
} from '@/api';
import { useCreatePriceErrorNotification } from '@/hooks/use-create-price-error-notification';
import { useNotification } from '@/hooks/use-notification';
import { ROUTES } from '@/routes';
import { normalizeApiError } from '@/utils/error-utils';
import { LP_MIN_THRESHOLD } from '@/utils/lp-validation';
import type { MRT_RowSelectionState } from 'material-react-table';
import { useCallback } from 'react';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';
import { CREATE_PRICE_PUBLISHED_ROWS_STORAGE_KEY } from './create-price-constants';
import {
  buildSessionSaveRequest,
  formatApprovalPayload,
  formatPriceChangePayload,
  getModifiedRowsForSession,
} from './create-price-helpers';
import type { CategorizedModifiedRows } from './create-price-helpers';
import {
  useDeleteCreatePriceSession,
  usePublishCreateAndGoToApprove,
  useSaveCreatePrices,
} from './create-price-queries';
import {
  type CreatePriceModalFilters,
  CreatePriceMode,
  CreatePriceQueryParams,
} from './create-price-search-params';
import type { RowData } from './types/create-price-table-types';

const formatNotificationMessage = (
  savedCount: number,
  approvedCount: number,
  isApproveNotification: boolean,
): string[] => {
  const messageParts: string[] = [];
  if (isApproveNotification) {
    if (approvedCount > 0)
      messageParts.push(
        `${approvedCount} ${approvedCount === 1 ? 'price' : 'prices'} approved`,
      );
  } else {
    if (savedCount > 0)
      messageParts.push(
        `${savedCount} ${savedCount === 1 ? 'price' : 'prices'} published`,
      );
    if (approvedCount > 0)
      messageParts.push(
        `${approvedCount} ${approvedCount === 1 ? 'price' : 'prices'} approved`,
      );
  }
  return messageParts;
};

const isValidationError = (
  body: unknown,
): body is CreatePriceValidationProblemDetails => {
  return (
    typeof body === 'object' &&
    body !== null &&
    'errors' in body &&
    Array.isArray((body as CreatePriceValidationProblemDetails).errors)
  );
};

interface UseCreatePriceSaveParams {
  mode: CreatePriceMode;
  filters: CreatePriceModalFilters;
  validFrom: Date;
  data: Map<string, RowData>;
  comparisonBaseline: Map<string, RowData>;
  categorizedChanges: CategorizedModifiedRows;
  setData: (data: Map<string, RowData>) => void;
  setIsPublishedLocal: (value: boolean) => void;
  setIsSaved: (value: boolean) => void;
  setShowSaveModal: (value: boolean) => void;
  setRowSelection: (selection: {
    state: MRT_RowSelectionState;
    items: RowData[];
  }) => void;
  setValidationErrors: (errors: CreatePriceValidationErrorInfo[]) => void;
}

export const useCreatePriceSave = ({
  mode,
  filters,
  validFrom,
  data,
  comparisonBaseline,
  categorizedChanges,
  setData,
  setIsPublishedLocal,
  setIsSaved,
  setShowSaveModal,
  setRowSelection,
  setValidationErrors,
}: UseCreatePriceSaveParams) => {
  const isApproveMode = mode === CreatePriceMode.Approve;
  const showNotification = useNotification();
  const showError = useCreatePriceErrorNotification();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { mutateAsync: saveCreatePrices, isPending: isSaving } =
    useSaveCreatePrices();
  const { mutateAsync: publishAndGoToApprove } =
    usePublishCreateAndGoToApprove();
  const { mutate: deleteSession } = useDeleteCreatePriceSession(mode);

  const executeSave = useCallback(async (): Promise<{
    savedCount: number;
    approvedCount: number;
  } | null> => {
    const { priceChanges, statusOnlyApprovals } = categorizedChanges;

    if (priceChanges.length === 0 && statusOnlyApprovals.length === 0)
      return null;

    let savedCount = 0;
    let approvedCount = 0;

    if (priceChanges.length > 0) {
      const payload = formatPriceChangePayload(
        priceChanges,
        validFrom,
        comparisonBaseline,
      );
      const response = await saveCreatePrices(payload);
      savedCount += response.savedCount ?? 0;
    }

    const priceChangesNeedingApproval = priceChanges.filter(
      (row) =>
        row.priceStatus === PriceStatus.Approved &&
        comparisonBaseline.get(row.primaryKey)?.priceStatus !==
          PriceStatus.Approved,
    );

    const allApprovals = [
      ...priceChangesNeedingApproval,
      ...statusOnlyApprovals,
    ];

    if (allApprovals.length > 0) {
      const payload = formatApprovalPayload(allApprovals, validFrom);
      const response = await saveCreatePrices(payload);
      approvedCount += response.savedCount ?? 0;
    }

    return { savedCount, approvedCount };
  }, [categorizedChanges, validFrom, comparisonBaseline, saveCreatePrices]);

  const handleSaveError = useCallback(
    (error: unknown) => {
      const normalized = normalizeApiError(error);
      if (
        normalized.kind === 'validation' &&
        normalized.status === 422 &&
        isValidationError(normalized.details)
      ) {
        setValidationErrors(normalized.details.errors ?? []);
      }
      showError(normalized, 'save prices');
    },
    [showError, setValidationErrors],
  );

  const hasZeroLpViolations = useCallback((): boolean => {
    const { priceChanges, statusOnlyApprovals } = categorizedChanges;

    let violationCount = 0;
    let hasApprovalViolation = false;

    for (const row of priceChanges) {
      if (
        row.lp < LP_MIN_THRESHOLD &&
        (comparisonBaseline.get(row.primaryKey)?.lp ?? 0) > 0
      ) {
        violationCount++;
      }
    }
    for (const row of statusOnlyApprovals) {
      if (row.lp < LP_MIN_THRESHOLD) {
        violationCount++;
        hasApprovalViolation = true;
      }
    }

    if (violationCount > 0) {
      const message = hasApprovalViolation
        ? `${violationCount} row(s) cannot be saved because List Price must be greater than 0.`
        : 'Saving failed because List Price(s) must be greater than 0.';
      showNotification({
        type: 'error',
        title: 'Oops!',
        description: message,
      });
      setShowSaveModal(false);
      return true;
    }
    return false;
  }, [
    categorizedChanges,
    comparisonBaseline,
    showNotification,
    setShowSaveModal,
  ]);

  const handleSave = useCallback(async () => {
    setValidationErrors([]);

    if (hasZeroLpViolations()) return;

    try {
      const result = await executeSave();
      if (!result) return;
      const { savedCount, approvedCount } = result;

      const updatedData = new Map(data);
      for (const [key, row] of updatedData) {
        const original = comparisonBaseline.get(key);
        if (
          original &&
          (row.rrp !== original.rrp ||
            row.lp !== original.lp ||
            row.priceStatus !== original.priceStatus)
        ) {
          updatedData.set(key, { ...row, hasPriceChange: true });
        }
      }
      setData(updatedData);

      setIsPublishedLocal(true);
      setIsSaved(true);
      setShowSaveModal(false);
      setRowSelection({ state: {}, items: [] });

      deleteSession();

      const modifiedRows = getModifiedRowsForSession(
        updatedData,
        comparisonBaseline,
      );
      if (modifiedRows.length > 0) {
        sessionStorage.setItem(
          CREATE_PRICE_PUBLISHED_ROWS_STORAGE_KEY,
          JSON.stringify(modifiedRows),
        );
      }

      const newParams = new URLSearchParams(searchParams);
      newParams.set(CreatePriceQueryParams.Published, 'true');
      newParams.delete(CreatePriceQueryParams.FromSession);
      navigate({ search: newParams.toString() }, { replace: true });

      const messageParts = formatNotificationMessage(
        savedCount,
        approvedCount,
        isApproveMode,
      );
      showNotification({
        type: 'success',
        title: isApproveMode ? 'Approved' : 'Published',
        description: messageParts.join(', '),
      });
    } catch (error) {
      if (isApproveMode) setShowSaveModal(false);
      handleSaveError(error);
    }
  }, [
    data,
    comparisonBaseline,
    isApproveMode,
    searchParams,
    executeSave,
    handleSaveError,
    hasZeroLpViolations,
    deleteSession,
    setData,
    setIsPublishedLocal,
    setIsSaved,
    setShowSaveModal,
    setRowSelection,
    setValidationErrors,
    navigate,
    showNotification,
  ]);

  const handlePublishAndGoToApprove = useCallback(async () => {
    setValidationErrors([]);

    if (hasZeroLpViolations()) return;

    try {
      const result = await executeSave();
      if (!result) return;
      const { savedCount, approvedCount } = result;

      const approveFilters = {
        ...filters,
        priceStatus: { approved: false, preliminary: true },
      };

      await publishAndGoToApprove(
        buildSessionSaveRequest(
          CreatePriceMode.Approve,
          approveFilters,
          data,
          comparisonBaseline,
        ),
      );

      setIsSaved(true);
      setShowSaveModal(false);

      const messageParts = formatNotificationMessage(
        savedCount,
        approvedCount,
        false,
      );
      showNotification({
        type: 'success',
        title: 'Published',
        description: messageParts.join(', '),
      });

      const newParams = new URLSearchParams(searchParams);
      newParams.set(CreatePriceQueryParams.Mode, CreatePriceMode.Approve);
      newParams.set(CreatePriceQueryParams.Preliminary, 'true');
      newParams.set(CreatePriceQueryParams.FromSession, 'true');
      newParams.delete(CreatePriceQueryParams.Approved);
      newParams.delete(CreatePriceQueryParams.Published);

      navigate(
        {
          pathname: generatePath(ROUTES.CreatePrice.path),
          search: newParams.toString(),
        },
        { replace: true },
      );
    } catch (error) {
      handleSaveError(error);
    }
  }, [
    data,
    filters,
    comparisonBaseline,
    searchParams,
    executeSave,
    handleSaveError,
    hasZeroLpViolations,
    publishAndGoToApprove,
    setIsSaved,
    setShowSaveModal,
    setValidationErrors,
    navigate,
    showNotification,
  ]);

  return {
    handleSave,
    handlePublishAndGoToApprove,
    isSaving,
  };
};
